import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';

type Blocker =
  | { code: 'ACCOUNT_DELETE_PLATFORM_ROLE' }
  | { code: 'ACCOUNT_DELETE_SOLE_OWNER'; churchName: string };

type Eligibility = {
  canDelete: boolean;
  blockers: Blocker[];
};

export const AccountDeletePage = () => {
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);

  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const eligibility = useQuery({
    queryKey: ['account-deletion', 'eligibility'],
    queryFn: () => api.get<Eligibility>('/auth/me/account-deletion', { withoutChurch: true }),
  });

  const removeAccount = useMutation({
    mutationFn: () =>
      api.post<{ deleted: true }>(
        '/auth/me/delete',
        {
          confirmation: confirmation.trim().toLowerCase(),
          ...(user?.isTotpEnabled ? { totpCode: totpCode.trim() } : { password }),
        },
        { withoutChurch: true, idempotencyKey: crypto.randomUUID() },
      ),
    onSuccess: async () => {
      toast.success(t('deleteAccount.success'));
      await signOut();
      void navigate('/', { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (!user) return null;

  const blockers = eligibility.data?.blockers ?? [];
  const canDelete = eligibility.data?.canDelete ?? false;

  const blockerMessage = (blocker: Blocker): string => {
    if (blocker.code === 'ACCOUNT_DELETE_SOLE_OWNER') {
      return tErrors('ACCOUNT_DELETE_SOLE_OWNER', { churchName: blocker.churchName });
    }
    return tErrors('ACCOUNT_DELETE_PLATFORM_ROLE');
  };

  const openConfirm = () => {
    if (!canDelete) return;
    setConfirmOpen(true);
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <PageHeader
        title={t('deleteAccount.title')}
        description={t('deleteAccount.description')}
        breadcrumbs={[
          { label: t('account.tabs.profile'), to: '/account' },
          { label: t('deleteAccount.title') },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('deleteAccount.summaryTitle')}</CardTitle>
          <CardDescription>{t('deleteAccount.summaryDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-body text-fg-secondary">
          <p>{t('deleteAccount.summaryBody')}</p>
          <p>
            <Link to="/delete-me" className="text-brand underline-offset-2 hover:underline">
              {t('deleteAccount.learnMore')}
            </Link>
          </p>
        </CardContent>
      </Card>

      {eligibility.isLoading ? null : blockers.length > 0 ? (
        <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          <div className="space-y-2 text-body-sm text-fg">
            <p className="font-medium">{t('deleteAccount.blockedTitle')}</p>
            <ul className="list-disc space-y-1 pl-5">
              {blockers.map((blocker) => (
                <li key={`${blocker.code}-${'churchName' in blocker ? blocker.churchName : ''}`}>
                  {blockerMessage(blocker)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('deleteAccount.formTitle')}</CardTitle>
          <CardDescription>{t('deleteAccount.formDescription', { email: user.email })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField label={t('deleteAccount.confirmEmailLabel')} required>
            {(field) => (
              <Input
                id={field.id}
                type="email"
                autoComplete="off"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={user.email}
              />
            )}
          </FormField>

          {user.isTotpEnabled ? (
            <FormField label={t('signIn.totpCode')} required>
              {(field) => (
                <Input
                  id={field.id}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                />
              )}
            </FormField>
          ) : (
            <FormField label={t('field.password')} required>
              {(field) => (
                <PasswordInput
                  id={field.id}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </FormField>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="outline" asChild>
            <Link to="/account">{t('deleteAccount.cancel')}</Link>
          </Button>
          <Button
            variant="destructive"
            loading={removeAccount.isPending}
            disabled={!canDelete || confirmation.trim().length === 0}
            onClick={openConfirm}
          >
            {t('deleteAccount.submit')}
          </Button>
        </CardFooter>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('deleteAccount.confirmTitle', { name: `${user.firstName} ${user.lastName}`.trim() })}
        description={t('deleteAccount.confirmDescription')}
        confirmLabel={t('deleteAccount.submit')}
        cancelLabel={t('deleteAccount.cancel')}
        destructive
        loading={removeAccount.isPending}
        onConfirm={() => removeAccount.mutate()}
      />
    </div>
  );
};
